









import {
  Component, Prop, Vue,
} from 'vue-property-decorator';
import { FilterGetter, SearchGetter } from '@/store/getters';
import { SearchAction } from '@/store/actions';
import SearchResultElement from '@/models/SearchResultElement';
import SupportedBadge from '@/types/SupportedBadge';
import EcoFriendlyComponent from '@/components/SearchResults/Badges/EcoFriendlyComponent.vue';
import LogoFscComponent from '@/components/SearchResults/Badges/LogoFscComponent.vue';
import LogoPfscComponent from '@/components/SearchResults/Badges/LogoPfscComponent.vue';
import LogoFscPfscComponent from '@/components/SearchResults/Badges/LogoFscPfscComponent.vue';
import OvernightAvailableComponent
  from '@/components/SearchResults/Badges/OvernightAvailableComponent.vue';
import OnlineDesignComponent from '@/components/SearchResults/Badges/OnlineDesignComponent.vue';
import HtmlDataHelper from '@/helpers/HtmlDataHelper';
import TileTrackerHelper from '@/helpers/TileTrackerHelper';

@Component({
  name: 'external-slider-component',
  components: {
    'badge-ecofriendly-component': EcoFriendlyComponent,
    'badge-logofsc-component': LogoFscComponent,
    'badge-logopfsc-component': LogoPfscComponent,
    'badge-logofscpfsc-component': LogoFscPfscComponent,
    'badge-overnightavailable-component': OvernightAvailableComponent,
    'badge-onlinedesign-component': OnlineDesignComponent,
  },
})
export default class ExternalSliderComponent extends Vue {
  @Prop({ default: [] })
  protected externalSearchResults: Array<SearchResultElement> | undefined;

  private noMoreResultsExternal = false;

  private increment = 0;

  // <editor-fold desc="Init">
  private created() {
    this.$nextTick(() => {
      if (window.jQuery && window.Foundation) {
        const sliderExternal = window.jQuery('#slick-slider-external')
          .slick(this.slickOptions)
          .removeClass('slick-slider');
        this.generateSlideHtml(this.externalSearchResults);
        sliderExternal.on('afterChange', (event: Event, slick: {
          slideCount: number;
          options: { slidesToShow: number };
        }, currentSlide: number) => {
          if (currentSlide >= (slick.slideCount - slick.options.slidesToShow)
            && !this.noMoreResultsExternal) {
            if (
              slick.slideCount < this.$store.getters[SearchGetter.ExternalTotalNumberOfResults]) {
              this.$store.dispatch(SearchAction.ExternalLoadMore).then((results) => {
                if (window.jQuery) {
                  this.generateSlideHtml(results);
                }
              });
              Vue.set(this, 'noMoreResultsExternal', false);
            } else {
              Vue.set(this, 'noMoreResultsExternal', true);
            }
          }
        });
      }
    });
  }

  // </editor-fold>

  // <editor-fold desc="Private">
  private slickOptions = {
    infinite: true,
    adaptiveHeight: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    dots: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    ],
    prevArrow: `<button type="button" class="slick-prev align-middle slick-arrow" aria-label="${this.$t('searchResults.slider.previousSlide')}"><span class="ux-icon--angle-left"></span></button>`,
    nextArrow: `<button type="button" class="slick-next align-middle slick-arrow" aria-label="${this.$t('searchResults.slider.nextSlide')}"><span class="ux-icon--angle-right"></span></button>`,
  };

  get isLoading(): boolean {
    return this.$store.getters[SearchGetter.IsLoading];
  }

  get externalIsLoading(): boolean {
    return this.$store.getters[SearchGetter.ExternalIsLoading];
  }

  // eslint-disable-next-line class-methods-use-this
  public prepareBadges(badges: Array<SupportedBadge>): Array<SupportedBadge> {
    return badges.filter(
      (badge: SupportedBadge) => Object.values(SupportedBadge).includes(badge),
    );
  }

  private hasFilters() {
    let hasFilters = 'Not Filtered';
    if (this.$store.getters[FilterGetter.AppliedFilterOptions].size > 0) {
      hasFilters = 'Filtered';
    }
    return hasFilters;
  }

  private tileTracker = new TileTrackerHelper(new HtmlDataHelper());

  private externalResultClick(element: HTMLElement) {
    if (element.getAttribute('data-type') === 'product') {
      this.tileTracker.sendGoogleDataLayer({
        event: 'eec.productClick',
        ecommerce: {
          currencyCode: 'currencyCode',
          click: {
            actionField: {
              list: `resultpage|additionalProductCollection|${this.hasFilters()}`,
            },
            products: [{
              category: HtmlDataHelper.getCategory(element),
              name: `${HtmlDataHelper.getTileName(element)}`,
              position: `${HtmlDataHelper.getTilePosition(element)}`,
            }],
          },
        },
      });
    } else {
      this.tileTracker.sendGoogleDataLayer({
        event: 'eec.promotionClick',
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: `resultpage|additionalProductCollection|${this.hasFilters()}`,
                name: `${HtmlDataHelper.getTileName(element)}`,
                creative: `${HtmlDataHelper.getTileUrl(element)}`,
                position: `${HtmlDataHelper.getTilePosition(element)}`,
              },
            ],
          },
        },
      });
    }
  }

  private generateSlideHtml(result: Array<SearchResultElement> | undefined): void {
    if (result) {
      result.forEach((searchResult) => {
        let html = '<div class="slick-list">';
        html += '<div class="tile-container no-tracking margin-bottom-0">';
        html += `<a href="${searchResult.urlPath}"
                class="tile tile--auto search-result"
                style="height: 100%;"
                data-id="resultpage|additionalProductCollection|${this.hasFilters()}"
                data-fc="${searchResult.itemId}"
                data-type="${searchResult.type}"
                data-category="${HtmlDataHelper.createAnalyticsCategory(searchResult)}"
                data-increment="${this.increment}"
                >`;
        html += '<div class="tile__image-container image-container image-container--8x5">';
        html += `<img src="${searchResult.imageUrlPath}" alt="${searchResult.title}"/>`;
        html += '</div>';
        html += '<div class="tile__content">';

        html += `<div class="tile__next-step">
                        ${this.$t(`searchResults.taglines.${searchResult.type}`)}
                     </div>
                     <div class="tile__headline">
                        ${searchResult.title}
                     </div>`;
        if (searchResult.badges.length > 0) {
          html += '<div class="tile__tags tags hide-for-small-only">';
          this.prepareBadges(searchResult.badges).forEach((badge) => {
            // eslint-disable-next-line default-case
            switch (badge) {
              case 'ecoFriendly':
                html += `<span class="tags__tag -nature">${this.$t('searchResults.badges.ecofriendly')}</span>`;
                break;
              case 'logoFsc':
                html += `<span class="tags__tag -nature">${this.$t('searchResults.badges.fsc')}</span>`;
                break;
              case 'logoFscPfsc':
                html += `<span class="tags__tag -nature">${this.$t('searchResults.badges.fsc_pfsc')}</span>`;
                break;
              case 'logoPfsc':
                html += `<span class="tags__tag -nature">${this.$t('searchResults.badges.pfsc')}</span>`;
                break;
              case 'onlineDesign':
                html += `<span class="tags__tag fly-tag-obj-default-class" href="${searchResult.onlineDesignUrl}">`;
                html += `${this.$t('searchResults.badges.onlineDesign')}`;
                html += '</span>';
                break;
              case 'overnightAvailable':
                html += `<span class="tags__tag -overnight">${this.$t('searchResults.badges.overnight')}</span>`;
                break;
            }
          });
          html += '</div>';
        }
        if (searchResult.stopper !== '') {
          html += `<div class="tile__overlays">
                      <div class="tile__stoppers stoppers">
                        <span class="stoppers__stopper">${searchResult.stopper}</span>
                      </div>
                   </div>`;
        }
        html += '</div>';
        html += '</a>';
        html += '</div>'
          + '</div>';
        if (window.jQuery) {
          window.jQuery('#slick-slider-external').slick('slickAdd', html);
          window.jQuery('#slick-slider-external .search-result').on('click', (e: Event) => {
            if (window.jQuery) {
              e.stopImmediatePropagation();
              this.externalResultClick(e.currentTarget as HTMLElement);
            }
          });
        }
        this.increment += 1;
      });
    }
  }
}
